import { useEffect, useState } from 'react';
import { getOfficeAddinAgent } from '../api/client';

export type HostAgentState =
  | { status: 'idle'; error: null }
  | { status: 'loading'; error: null }
  | { status: 'ready'; agentId: string; error: null }
  | { status: 'error'; error: string };

/**
 * Resolves a host-pinned blueprint id to its deployed runtime id via
 * GET /office-addin/agents/{blueprintId}. The runtime id is what we pass
 * to /agents/{id}/chat.
 *
 * - When `blueprintId` is undefined (e.g. Outlook), returns 'idle'.
 * - On 4xx/5xx, returns 'error' with the message — the shell renders
 *   <UnsupportedHost /> in that case.
 * - Caches the success for the lifetime of the component (no re-fetches).
 */
export function useHostAgentId(blueprintId: string | undefined): HostAgentState {
  const [state, setState] = useState<HostAgentState>({ status: 'idle', error: null });

  useEffect(() => {
    if (!blueprintId) {
      setState({ status: 'idle', error: null });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading', error: null });
    (async () => {
      const res = await getOfficeAddinAgent(blueprintId);
      if (cancelled) return;
      if (res.error || !res.data) {
        setState({ status: 'error', error: res.error || 'No response from resolver' });
      } else {
        setState({ status: 'ready', agentId: res.data.agentId, error: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blueprintId]);

  return state;
}